import { Controller, type Control, type UseFormRegister } from "react-hook-form";
import { RichTextEditor } from "@legal-500/legal500-design-system";
import type { TranslationTarget, TranslationsView } from "@/api/researchCms.service";
import type { OrgFormValues } from "../../schemas/orgViewSchemas";
import { languageName } from "../../utils/languageName";
import shared from "../cmsShared.module.css";
import styles from "./TranslationsTab.module.css";

export type TranslationsTabProps = {
  view: TranslationsView | null;
  loading: boolean;
  errorMessage: string | null;
  gateMessage: string | null;
  autoPopulated: boolean;
  canEdit: boolean;
  dirty: boolean;
  busyTarget: "all" | string | null;
  control: Control<OrgFormValues>;
  register: UseFormRegister<OrgFormValues>;
  onTranslateAll: () => void;
  onRegenerate: (target: TranslationTarget) => void;
};

export const GATE_MESSAGE =
  "Translations not available yet. English content must be marked Ready for Translation.";
export const AUTO_POPULATED_MESSAGE =
  "Source language matches output language — content copied directly.";
export const DIRTY_HINT = "Save or discard your edits before regenerating.";

type TranslationMeta = { translationSource: "auto" | "manual"; stale: boolean } | null;

const noop = () => undefined;

function Notice({ role, message }: { role: "status" | "alert"; message: string }) {
  const tone = role === "alert" ? ` ${styles.alert}` : "";
  return (
    <div className={`${shared.notice} ${styles.notice}${tone}`} role={role}>
      <p className={shared.noticeMessage}>{message}</p>
    </div>
  );
}

function Badges({ translation }: { translation: TranslationMeta }) {
  if (!translation) return null;
  return (
    <>
      <span className={styles.badge}>
        {translation.translationSource === "auto" ? "Auto-translated" : "Manually edited"}
      </span>
      {translation.stale && (
        <span className={`${styles.badge} ${styles.badgeStale}`}>
          English changed since translation
        </span>
      )}
    </>
  );
}

export function TranslationsTab({
  view,
  loading,
  errorMessage,
  gateMessage,
  autoPopulated,
  canEdit,
  dirty,
  busyTarget,
  control,
  register,
  onTranslateAll,
  onRegenerate,
}: TranslationsTabProps) {
  if (gateMessage) return <Notice role="status" message={gateMessage} />;
  if (!view) {
    if (loading) {
      return (
        <p className={styles.empty} role="status">
          Loading translations…
        </p>
      );
    }
    return errorMessage ? <Notice role="alert" message={errorMessage} /> : null;
  }

  const sourceName = languageName(view.sourceLanguage);
  const targetName = languageName(view.targetLanguage);
  const commentarySource = view.commentary.source;
  const totalFields = (commentarySource ? 1 : 0) + view.matters.length;
  const translatedFields =
    (view.commentary.translation ? 1 : 0) +
    view.matters.filter(matter => matter.translation !== null).length;
  const hasTranslation = view.translationContentId !== null;
  const actionsBlocked = !canEdit || dirty || busyTarget !== null;
  const actionTitle = dirty ? DIRTY_HINT : undefined;

  const regenerateButton = (target: TranslationTarget, busyKey: string) =>
    canEdit && (
      <button
        type="button"
        className={`${shared.discardButton} ${styles.actionButton}`}
        onClick={() => onRegenerate(target)}
        disabled={actionsBlocked}
        title={actionTitle}
      >
        {busyTarget === busyKey ? "Regenerating…" : "Regenerate"}
      </button>
    );

  return (
    <div className={styles.tab}>
      <div className={styles.header}>
        <div>
          <h2 className={`${shared.sectionHeading} ${shared.headingInline}`}>
            {sourceName} → {targetName}
          </h2>
          <p className={styles.summary}>
            {translatedFields} of {totalFields} fields have content
          </p>
        </div>
        {canEdit && (
          <button
            type="button"
            className={`${shared.saveButton} ${styles.actionButton}`}
            onClick={onTranslateAll}
            disabled={actionsBlocked}
            title={actionTitle}
          >
            {busyTarget === "all"
              ? "Translating…"
              : hasTranslation
                ? "Regenerate all"
                : "Translate"}
          </button>
        )}
      </div>

      {autoPopulated && <Notice role="status" message={AUTO_POPULATED_MESSAGE} />}
      {errorMessage && <Notice role="alert" message={errorMessage} />}
      {!hasTranslation && !canEdit && <p className={styles.empty}>No translation yet.</p>}

      {commentarySource && (
        <section className={`${shared.sectionCard} ${styles.section}`}>
          <div className={styles.fieldHeader}>
            <div className={styles.fieldTitle}>
              <h3 className={`${shared.sectionSubheading} ${shared.headingInline}`}>
                Editorial content
              </h3>
              <Badges translation={view.commentary.translation} />
            </div>
            {regenerateButton("commentary", "commentary")}
          </div>
          <div className={styles.columns}>
            <div className={styles.column}>
              <span className={styles.columnLabel}>{sourceName}</span>
              <RichTextEditor
                value={commentarySource.postContent}
                onChange={noop}
                preset="full"
                entityReferences
                htmlTab
                minHeight={200}
                label=""
                ariaLabel={`Editorial content (${sourceName})`}
                disabled
              />
            </div>
            <div className={styles.column}>
              <span className={styles.columnLabel}>{targetName}</span>
              <Controller
                control={control}
                name="translations.commentary.postContent"
                render={({ field }) => (
                  <RichTextEditor
                    value={field.value}
                    onChange={field.onChange}
                    preset="full"
                    entityReferences
                    htmlTab
                    toolbar={false}
                    minHeight={200}
                    label=""
                    ariaLabel={`Editorial content (${targetName})`}
                    disabled={!canEdit}
                  />
                )}
              />
            </div>
          </div>
          {commentarySource.clerksCommentary !== null && (
            <div className={styles.columns}>
              <div className={styles.column}>
                <span className={styles.columnLabel}>Clerks' commentary · {sourceName}</span>
                <textarea
                  className={shared.textarea}
                  rows={4}
                  value={commentarySource.clerksCommentary}
                  aria-label={`Clerks' commentary (${sourceName})`}
                  readOnly
                />
              </div>
              <div className={styles.column}>
                <span className={styles.columnLabel}>Clerks' commentary · {targetName}</span>
                <textarea
                  {...register("translations.commentary.clerksCommentary")}
                  className={shared.textarea}
                  rows={4}
                  aria-label={`Clerks' commentary (${targetName})`}
                  readOnly={!canEdit}
                />
              </div>
            </div>
          )}
        </section>
      )}

      {view.matters.map((matter, index) => {
        const title = `Matter ${index + 1}`;
        return (
          <section key={matter.matterId} className={`${shared.sectionCard} ${styles.section}`}>
            <div className={styles.fieldHeader}>
              <div className={styles.fieldTitle}>
                <h3 className={`${shared.sectionSubheading} ${shared.headingInline}`}>{title}</h3>
                <Badges translation={matter.translation} />
              </div>
              {regenerateButton({ matterId: matter.matterId }, matter.matterId)}
            </div>
            <div className={styles.columns}>
              <div className={styles.column}>
                <span className={styles.columnLabel}>{sourceName}</span>
                <textarea
                  className={`${shared.textarea} ${styles.matterInput}`}
                  value={matter.source.summary ?? ""}
                  aria-label={`${title} (${sourceName})`}
                  readOnly
                />
              </div>
              <div className={styles.column}>
                <span className={styles.columnLabel}>{targetName}</span>
                <textarea
                  {...register(`translations.matters.${matter.matterId}`)}
                  className={`${shared.textarea} ${styles.matterInput}`}
                  aria-label={`${title} (${targetName})`}
                  readOnly={!canEdit}
                />
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}
